import { useEffect, useMemo, useState } from 'react';
import FilterPanel from '../../FilterLayout/FilterPanel';

import { useWidgetsModalContext } from '../../../../../shared/providers/WidgetsModalProvider';
import useFilters from '../../../../../utilities/hooks/useFilters';

import {
  IOrganisation,
  IData,
  IDepartment,
  OptionType,
} from '../../../../../shared/types/filters';
import { FilterKeys } from '../../../../../shared/constants/filter/filter';
import { convertToNestedFormat } from '../../../../../utilities/helper/departments';
import { useFilterData } from '../../../../../utilities/hooks/useFilterData';

const createDeptOption = ({
  name,
  id,
  children,
}: IDepartment): OptionType<number> => {
  return {
    label: name,
    value: id,
    ...(children && {
      children: children.map((child) => createDeptOption(child)),
    }),
  };
};

const flattenDepartmentArray = (deptArray?: IDepartment[]) => {
  const result = [] as IDepartment[];

  if (deptArray) {
    deptArray.forEach(function (dept) {
      result.push(dept);
      if (dept.children?.length) {
        result.push(...flattenDepartmentArray(dept.children));
      }
    });
  }

  return result;
};

export default function DepartmentFilter({
  orgs,
  organisations,
}: {
  orgs?: IDepartment[];
  organisations?: IOrganisation[];
}) {
  const [queryData, setQueryData] = useState<IData>({ teams: [] });

  const { selectedAccorionFilter } = useWidgetsModalContext();
  const {
    selectedDepartments,
    toggleDepartmentAndChildren,
    selectOrDeselectAllDepartments,
    selectedOrganisations,
  } = useFilters();
  const { configData } = useFilterData();

  const organisationGroups = useMemo(() => {
    const hasSelection = Array.from(selectedOrganisations.values()).some(
      Boolean,
    );
    return organisations
      ?.filter(
        (organisation) =>
          !hasSelection || selectedOrganisations.get(organisation.id),
      )
      .map((organisation) => ({
        ...organisation,
        teams: convertToNestedFormat(organisation.teams ?? []),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [organisations, selectedOrganisations]);

  const departmentOptions = useMemo(() => {
    const createOptions = (teams: IDepartment[]) =>
      teams
        .map(createDeptOption)
        .sort((a, b) => a.label.localeCompare(b.label));
    if (organisationGroups) {
      return organisationGroups
        .filter((organisation) => organisation.teams.length > 0)
        .map((organisation) => ({
          label: organisation.name,
          value: organisation.id,
          selectable: false,
          children: createOptions(organisation.teams),
        }));
    }
    return createOptions(queryData.teams ?? []);
  }, [queryData.teams, organisationGroups]);

  const flatDepartmentArray = useMemo(
    () =>
      flattenDepartmentArray(
        organisationGroups
          ? organisationGroups.flatMap((organisation) => organisation.teams)
          : queryData.teams,
      ),
    [queryData.teams, organisationGroups],
  );

  useEffect(() => {
    if (!organisationGroups && !queryData.teams?.length) return;
    const availableIds = new Set(
      flatDepartmentArray.map((department) => department.id),
    );
    const retainedDepartments = new Map(
      [...selectedDepartments].filter(([id]) => availableIds.has(id)),
    );
    if (retainedDepartments.size !== selectedDepartments.size) {
      selectOrDeselectAllDepartments(retainedDepartments);
    }
  }, [flatDepartmentArray]);

  function getSelectedDepts() {
    const selected = [] as OptionType<number>[];
    Array.from(selectedDepartments)
      .filter(([_deptId, exists]) => exists)
      .forEach(([deptId]) => {
        const res = flatDepartmentArray.find((dept) => dept.id === deptId);
        if (res) {
          selected.push({
            label: getQualifiedName(res),
            value: res.id,
            children: res.children
              ? res.children.map((child) => createDeptOption(child))
              : undefined,
          });
        }
      });
    return selected;
  }

  const isDepartmentSelected = (id: number) =>
    selectedDepartments.get(id) ?? false;

  function onDepartmentChange(id: number) {
    const selectedDept = flatDepartmentArray.find((dept) => dept.id === id);

    if (selectedDept) {
      const qualifiedName = getQualifiedName(selectedDept);
      toggleDepartmentAndChildren({ ...selectedDept, name: qualifiedName });
    }
  }

  // Function to recursively update the name with parent names
  function getQualifiedName(node: any): string {
    const parentNames = [];
    let currentNode = node;

    while (currentNode?.parentTeamId !== null) {
      const parentDept = flatDepartmentArray.find(
        (dept) => dept.id === currentNode.parentTeamId,
      );
      if (parentDept) {
        parentNames.unshift(parentDept.name);
        currentNode = parentDept;
      } else break;
    }

    const organisation = organisationGroups?.find((group) =>
      flattenDepartmentArray(group.teams).some((team) => team.id === node.id),
    );
    if (organisation) parentNames.unshift(organisation.name);
    return [...parentNames, node.name.split(' - ').slice(-1)[0]].join(' - ');
  }

  const selectAll = (
    isChecked: boolean,
    optionsToSelect: number[] = [],
    isMatching?: boolean,
  ) => {
    if (isChecked || (!isChecked && optionsToSelect.length === 0)) {
      const updatedDepartments = isMatching
        ? new Map(
            [...selectedDepartments].filter(
              ([id]) => !optionsToSelect.includes(id),
            ),
          )
        : new Map();
      selectOrDeselectAllDepartments(updatedDepartments);
    } else if (optionsToSelect.length > 0) {
      const selectedDepts = isMatching
        ? new Map(selectedDepartments)
        : new Map();
      optionsToSelect.forEach((id: any) => {
        selectedDepts.set(id, true);
      });
      selectOrDeselectAllDepartments(selectedDepts);
    } else {
      const newSelectedDepts = new Map();
      flatDepartmentArray.forEach(({ id }) => {
        newSelectedDepts.set(id, true);
      });
      selectOrDeselectAllDepartments(newSelectedDepts);
    }
  };

  useEffect(() => {
    if (orgs)
      setQueryData({
        teams: orgs,
      });
  }, [orgs]);

  useEffect(() => {
    configData &&
      Array.from(selectedOrganisations.values()).every(
        (value) => value === false,
      ) &&
      setQueryData(configData);
  }, [configData, selectedAccorionFilter]);

  if (!queryData?.teams) {
    return null;
  }

  return (
    <FilterPanel<number>
      name="department"
      filterKey={FilterKeys.department}
      options={departmentOptions}
      isSelected={isDepartmentSelected}
      onSelectedChange={onDepartmentChange}
      selectedItems={getSelectedDepts()}
      selectAllItems={selectAll}
      flatItemArray={flatDepartmentArray}
    />
  );
}
